import type { MaintenanceEvent } from '~/api/types'
import type { ImpactRow } from './types'
import { escapeHtml, formatDateTime } from './utils'

function statusLabel(row: ImpactRow): string {
    if (!row.affected) return '영향 없음'
    return row.affected.recheck_required ? '재점검 필요' : '유지'
}

function statusClass(row: ImpactRow): string {
    if (!row.affected) return 'none'
    return row.affected.recheck_required ? 'recheck' : 'keep'
}

/** 영향받은(=affected) 항목만 카드로 상세 표기. 현재/변경 후·근거·권고를 포함한다. */
function buildImpactCards(impactRows: ImpactRow[]): string {
    const affectedRows = impactRows.filter((row) => row.affected)
    if (affectedRows.length === 0) {
        return '<p class="empty">영향받는 점검 항목이 없습니다. 기존 관리 기준을 유지해도 됩니다.</p>'
    }
    return affectedRows
        .map((row) => {
            const aff = row.affected!
            const number = escapeHtml(row.item.itemNumber ?? '')
            const title = escapeHtml(aff.title ?? row.item.title)
            const path = escapeHtml(aff.checklistPath ?? '-')
            const basis = escapeHtml(aff.checklistBasis ?? '-')
            const current = escapeHtml(aff.current_state ?? '-')
            const after = escapeHtml(aff.expected_change ?? '-')
            const recommendation = aff.recommendation
                ? `<div class="recommend"><span class="rk">권고</span>${escapeHtml(aff.recommendation)}</div>`
                : ''
            return `
            <section class="card ${statusClass(row)}">
              <header class="card-head">
                <div>
                  <h3>${title}</h3>
                  <p class="meta">${number ? number + ' · ' : ''}${path} · ${basis}</p>
                </div>
                <span class="badge ${statusClass(row)}">${escapeHtml(statusLabel(row))}</span>
              </header>
              <div class="flow">
                <div class="cell"><div class="cl">현재</div><div class="cv">${current}</div></div>
                <div class="arrow">&rarr;</div>
                <div class="cell after"><div class="cl">변경 후</div><div class="cv">${after}</div></div>
              </div>
              ${recommendation}
            </section>`
        })
        .join('')
}

/** 전체 체크리스트 관점의 영향/유지/영향 없음 요약 표. */
function buildOverviewTable(impactRows: ImpactRow[]): string {
    const rows = impactRows
        .map((row) => {
            const score = row.score
                ? `${row.score.score ?? 0}/${row.score.maxScore}`
                : '-'
            return `<tr class="${statusClass(row)}"><td>${escapeHtml(row.item.itemNumber ?? '')}</td><td>${escapeHtml(row.item.title)}</td><td>${escapeHtml(statusLabel(row))}</td><td>${escapeHtml(score)}</td><td>${escapeHtml(row.affected?.checklistPath ?? '-')}</td></tr>`
        })
        .join('')
    return `<table><thead><tr><th>번호</th><th>체크리스트 항목</th><th>영향</th><th>현재 점수</th><th>근거</th></tr></thead><tbody>${rows}</tbody></table>`
}

export const REPORT_STYLE = `
:root{color-scheme:light}
/* 앱 셸(app.css)의 html,body,#root{height:100%}·body{overflow:hidden}를 덮어
   standalone 보고서가 문서 전체 높이로 자라며 스크롤되게 한다. */
html,body,#root{height:auto;min-height:100%;overflow:visible}
*{box-sizing:border-box}
body{font-family:'Apple SD Gothic Neo','Malgun Gothic',system-ui,sans-serif;margin:0;color:#1f2328;background:#f3f4f6;line-height:1.6}
.toolbar{position:sticky;top:0;display:flex;justify-content:space-between;align-items:center;gap:12px;background:#111827;color:#fff;padding:12px 24px;box-shadow:0 1px 4px rgba(0,0,0,.2)}
.toolbar span{font-size:13px;opacity:.85}
.toolbar button{font:inherit;font-size:14px;font-weight:600;cursor:pointer;border:0;border-radius:6px;padding:8px 16px;background:#2563eb;color:#fff}
.toolbar button:hover{background:#1d4ed8}
.page{max-width:794px;margin:24px auto;background:#fff;padding:40px 44px;box-shadow:0 1px 6px rgba(0,0,0,.12)}
h1{font-size:22px;margin:0 0 4px}
.subtitle{color:#6b7280;font-size:13px;margin:0 0 20px}
.summary{border:1px solid #e5e7eb;border-radius:8px;padding:16px 18px;margin-bottom:24px;background:#f9fafb}
.summary h2{font-size:14px;margin:0 0 8px;color:#374151}
.summary .desc{white-space:pre-wrap;font-size:14px}
.counts{display:flex;gap:10px;margin-top:14px;flex-wrap:wrap}
.count{flex:1;min-width:120px;border:1px solid #e5e7eb;border-radius:6px;padding:10px 12px;text-align:center;background:#fff}
.count b{display:block;font-size:22px}
.count.recheck b{color:#b45309}
.count.keep b{color:#15803d}
.count.none b{color:#6b7280}
.count small{color:#6b7280}
h2.section{font-size:16px;margin:28px 0 12px;border-bottom:2px solid #111827;padding-bottom:6px}
.card{border:1px solid #e5e7eb;border-radius:8px;padding:16px 18px;margin-bottom:14px;break-inside:avoid}
.card.recheck{border-left:4px solid #f59e0b}
.card.keep{border-left:4px solid #22c55e}
.card-head{display:flex;justify-content:space-between;align-items:flex-start;gap:12px}
.card-head h3{font-size:15px;margin:0}
.meta{color:#6b7280;font-size:12px;margin:4px 0 0}
.badge{font-size:12px;font-weight:700;padding:3px 10px;border-radius:999px;white-space:nowrap}
.badge.recheck{background:#fef3c7;color:#b45309}
.badge.keep{background:#dcfce7;color:#15803d}
.flow{display:grid;grid-template-columns:1fr 28px 1fr;align-items:stretch;gap:8px;margin-top:12px}
.cell{border:1px solid #e5e7eb;border-radius:6px;padding:10px 12px;background:#f9fafb}
.cell.after{background:#eff6ff;border-color:#bfdbfe}
.cl{font-size:11px;font-weight:700;color:#6b7280}
.cell.after .cl{color:#2563eb}
.cv{font-size:13px;margin-top:4px;white-space:pre-wrap}
.arrow{display:grid;place-items:center;color:#9ca3af}
.recommend{margin-top:12px;background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;padding:10px 12px;font-size:13px}
.rk{font-weight:700;margin-right:8px}
.empty{color:#6b7280;font-size:14px}
table{border-collapse:collapse;width:100%;margin-top:8px;font-size:12px}
th,td{border:1px solid #e5e7eb;padding:8px 10px;text-align:left;vertical-align:top}
th{background:#f3f4f6}
tr.recheck td:nth-child(3){color:#b45309;font-weight:700}
tr.keep td:nth-child(3){color:#15803d;font-weight:600}
tr.none td:nth-child(3){color:#9ca3af}
@media print{
  body{background:#fff}
  .toolbar{display:none}
  .page{box-shadow:none;margin:0;max-width:none;padding:0}
  @page{size:A4;margin:16mm}
}
`

/**
 * 보고서 본문(툴바 + page div) HTML 문자열을 만든다. `<style>`·`<html>` 래퍼는 제외하며,
 * standalone 라우트(`/maintenance/report`)에서 REPORT_STYLE 과 함께 렌더한다.
 * 인쇄는 standalone 페이지가 직접 제어하므로 자동 인쇄 스크립트는 포함하지 않는다.
 */
export function buildImpactReportBody(
    activeEvent: MaintenanceEvent | null,
    impactRows: ImpactRow[]
): string {
    const affectedCount = impactRows.filter((row) => row.affected).length
    const recheckCount = impactRows.filter(
        (row) => row.affected?.recheck_required
    ).length
    const keepCount = affectedCount - recheckCount
    const noneCount = impactRows.length - affectedCount
    const changeDesc = escapeHtml(activeEvent?.changeDescription ?? '-')
    const generatedAt = escapeHtml(formatDateTime(activeEvent?.createdAt))

    return `<div class="toolbar">
  <span>변경 영향 분석 보고서</span>
  <button type="button" onclick="window.print()">PDF로 저장 / 인쇄</button>
</div>
<div class="page">
  <h1>변경 영향 분석 보고서</h1>
  <p class="subtitle">HACCP 변경 검토 AI · 분석 일시 ${generatedAt}</p>

  <div class="summary">
    <h2>변경 요약</h2>
    <div class="desc">${changeDesc}</div>
    <div class="counts">
      <div class="count recheck"><b>${recheckCount}</b><small>재점검 필요</small></div>
      <div class="count keep"><b>${keepCount}</b><small>유지</small></div>
      <div class="count none"><b>${noneCount}</b><small>영향 없음</small></div>
    </div>
    <p class="subtitle" style="margin-top:12px">전체 ${impactRows.length}개 점검 항목 중 ${affectedCount}개 영향</p>
  </div>

  <h2 class="section">영향 항목 상세</h2>
  ${buildImpactCards(impactRows)}

  <h2 class="section">전체 체크리스트 관점</h2>
  ${buildOverviewTable(impactRows)}
</div>`
}
